/**
 * GuestExperiencePage — the redesign's flagship surface (the differentiator).
 *
 * Consolidates Guidebook, In-room Display, Recommendations (Places) and House
 * Rules into one tabbed surface with the "write once · every surface" hero
 * (phone guidebook + e-ink display + TV mockups) from the design handoff.
 *
 * The tab bodies render the existing, fully-functional pages via nested routes
 * (<Outlet/>), so every control — TRMNL token lifecycle, TV slide editor, night
 * mode, multilingual fallbacks, Places AI-research engine + plan gate, the House
 * Rules surface-routing model — is preserved with its DemoGuard/plan gates
 * intact. Each tab body is reskinned to the handoff layout in follow-up slices.
 */

import { NavLink, Outlet } from "react-router"
import { cn } from "@/lib/utils"
import { Sparkles, CheckCircle2 } from "lucide-react"

const TABS = [
  { to: "", label: "Guidebook", end: true },
  { to: "display", label: "In-room Display" },
  { to: "places", label: "Recommendations" },
  { to: "rules", label: "House Rules" },
]

export function GuestExperiencePage() {
  return (
    <div className="mx-auto max-w-[1200px] px-4 pb-16 pt-6 md:px-7">
      <GuestExperienceHero />

      {/* Tabs */}
      <div className="mb-[18px] flex gap-1 border-b border-border">
        {TABS.map((t) => (
          <NavLink
            key={t.to || "index"}
            to={t.to}
            end={t.end}
            className={({ isActive }) =>
              cn(
                "relative px-[15px] py-[11px] text-[13.5px] font-semibold transition-colors",
                isActive ? "text-foreground" : "text-muted-foreground hover:text-ink-2",
              )
            }
          >
            {({ isActive }) => (
              <>
                {t.label}
                <span
                  className={cn(
                    "absolute inset-x-2.5 -bottom-px h-0.5 rounded-full",
                    isActive ? "bg-brand" : "bg-transparent",
                  )}
                />
              </>
            )}
          </NavLink>
        ))}
      </div>

      <Outlet />
    </div>
  )
}

function GuestExperienceHero() {
  return (
    <section
      className="relative mb-[18px] overflow-hidden rounded-card px-[30px] py-7 text-[#EAF3EE] shadow-sm"
      style={{ background: "radial-gradient(130% 150% at 100% 0%,#1c4c3b 0%,var(--sf-forest) 60%)" }}
    >
      <div
        className="pointer-events-none absolute inset-0"
        style={{ background: "radial-gradient(46% 66% at 86% 116%,rgba(30,200,148,.22),transparent 70%)" }}
      />
      <div className="relative grid grid-cols-1 items-center gap-6 lg:grid-cols-[minmax(0,0.95fr)_minmax(0,1.4fr)]">
        <div className="min-w-0">
          <span className="inline-flex items-center gap-1.5 rounded-full border border-white/[0.16] bg-white/10 px-3 py-1.5 text-[11px] font-semibold">
            Write once · every surface
          </span>
          <h2 className="mt-[15px] font-display text-[clamp(23px,2.6vw,29px)] font-bold leading-[1.1] tracking-[-0.025em] text-balance">
            Make your rental feel like a boutique hotel
          </h2>
          <p className="mt-3 max-w-[44ch] text-[14px] font-normal leading-[1.55] text-[#B9D0C6]">
            Set the tone once. Stayframe renders a warm, weather- and guest-aware welcome across the phone guidebook,
            the in-room display, and the TV — and reshapes it through the stay, automatically.
          </p>
          <div className="mt-[18px] flex items-center gap-2.5 rounded-xl border border-white/[0.12] bg-white/[0.07] px-3.5 py-[11px]">
            <CheckCircle2 className="h-[18px] w-[18px] flex-none text-[#9ff0c8]" strokeWidth={1.8} />
            <span className="text-[12.5px] font-medium leading-[1.4] text-[#CFE6DC]">
              AI writes the words — <strong className="font-semibold text-white">warm, weather-aware</strong> and in
              your voice, for every stay.
            </span>
          </div>
        </div>

        {/* Device mockups */}
        <div className="flex min-w-0 items-end justify-center gap-3">
          {/* Phone */}
          <div className="w-[102px] flex-none">
            <div
              className="rounded-[20px] border border-white/[0.12] p-2"
              style={{ background: "linear-gradient(168deg,#16382c,#0b231c)", boxShadow: "0 20px 40px -18px rgba(0,0,0,.55)" }}
            >
              <div className="flex h-[186px] flex-col rounded-[14px] bg-black/[0.18] px-3 py-[13px]">
                <div className="mx-auto mb-[11px] h-[3px] w-[34px] rounded-full bg-white/20" />
                <div className="text-[8px] font-medium uppercase tracking-[0.12em] text-[#7fb6a0]">Guidebook</div>
                <div className="mt-1.5 font-serif text-[14px] font-semibold leading-[1.1] text-[#EAF3EE]">
                  Welcome,<br />Sofia
                </div>
                <div className="mt-1.5 text-[8.5px] font-normal leading-[1.45] text-[#9dc2b3]">
                  The terrace catches the last sun around 8 — perfect with the wine we left.
                </div>
                <div className="mt-auto flex flex-col gap-1">
                  <span className="h-[5px] rounded-full bg-white/[0.15]" />
                  <span className="h-[5px] w-2/3 rounded-full bg-white/10" />
                </div>
              </div>
            </div>
            <div className="mt-[9px] text-center text-[10.5px] font-medium text-[#9dc2b3]">Phone guidebook</div>
          </div>

          {/* E-ink (wedge) */}
          <div className="w-[136px] flex-none">
            <div className="rounded-[11px] bg-[#15110c] p-[9px]" style={{ boxShadow: "0 24px 48px -18px rgba(0,0,0,.62)" }}>
              <div
                className="flex h-[184px] flex-col rounded-[5px] px-[13px] py-3.5 text-[#1a1a1a]"
                style={{ background: "linear-gradient(160deg,#ECE9E1,#DAD6CB)" }}
              >
                <div className="flex items-center justify-between">
                  <div className="text-[7.5px] font-medium uppercase tracking-[0.16em] text-[#6b6b6b]">In-room display</div>
                  <svg width="12" height="7" viewBox="0 0 26 13" fill="none"><rect x="1" y="1" width="21" height="11" rx="2.5" stroke="#6b6b6b" strokeWidth="1.2" /><rect x="3" y="3" width="14" height="7" rx="1" fill="#1a1a1a" /><rect x="23" y="4" width="2" height="5" rx="1" fill="#6b6b6b" /></svg>
                </div>
                <div className="mt-3 font-serif text-[19px] font-semibold leading-[1.02] text-[#111]">
                  Good evening,<br />Sofia.
                </div>
                <div className="mt-[9px] text-[9px] font-normal leading-[1.5] text-[#444]">
                  Night has settled over the marina. Quiet hours from 10 — sleep well.
                </div>
                <div className="mt-auto flex justify-between border-t border-black/[0.16] pt-2 font-mono text-[7.5px] font-semibold text-[#333]">
                  <span>WI-FI · MarinaLoft_5G</span><span>18°</span>
                </div>
              </div>
            </div>
            <div className="mt-[9px] flex items-center justify-center gap-1.5 text-center text-[10.5px] font-semibold text-[#9ff0c8]">
              <Sparkles className="h-[11px] w-[11px] fill-[#9ff0c8]" strokeWidth={0} />
              E-ink · only we ship this
            </div>
          </div>

          {/* TV */}
          <div className="w-[136px] flex-none">
            <div className="rounded-lg border border-white/10 bg-[#0a0a0a] p-1.5" style={{ boxShadow: "0 20px 40px -20px rgba(0,0,0,.5)" }}>
              <div
                className="relative flex h-24 flex-col justify-end overflow-hidden rounded-[3px] p-3 text-[#EAF3EE]"
                style={{ background: "linear-gradient(155deg,#1c4c3b,#081a14)" }}
              >
                <div className="absolute right-2.5 top-[9px] font-mono text-[7px] font-semibold text-[#9dc2b3]">20:14</div>
                <div className="font-serif text-[13px] font-semibold leading-[1.05]">Welcome to Marina Loft</div>
                <div className="mt-[3px] text-[8px] font-normal text-[#9dc2b3]">Open the guidebook on your phone →</div>
              </div>
            </div>
            <div className="mx-auto h-1 w-[38px] rounded-b-[3px] bg-[#0a0a0a]" />
            <div className="mt-[7px] text-center text-[10.5px] font-medium text-[#9dc2b3]">Living-room TV</div>
          </div>
        </div>
      </div>
    </section>
  )
}
